import { useMutationCertificateGenerate } from "@/api/certificate/certificate";
import { keyFactory } from "@/api/keyFactory";
import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/atoms/Dialog";
import useLoadingToast from "@/hooks/use-loading-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

interface GenerateCertificatesDialogProps {
  examId: number;
  registrationId?: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const GenerateCertificatesDialog = ({
  examId,
  registrationId,
  open,
  setOpen,
}: GenerateCertificatesDialogProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  useLoadingToast({
    message: "Génération des certificats en cours...",
    loading,
  });

  const { isPending, mutate } = useMutationCertificateGenerate({
    onSuccess: () => {
      setLoading(false);

      queryClient.invalidateQueries({
        queryKey: keyFactory.exam.base(),
      });

      queryClient.invalidateQueries({
        queryKey: keyFactory.registration.base(),
      });

      queryClient.invalidateQueries({
        queryKey: keyFactory.queues.jobs(),
      });

      toast.success("Génération demandée", {
        description:
          'Vous pouvez suivre l\'évolution de la tâche en cliquant sur le bouton  "Tâches"',
      });
    },
    onError: () => {
      setLoading(false);

      toast.error("Erreur", {
        description: "Les certificats n'ont pas pu être générés",
      });
    },
  });

  const onGenerate = () => {
    setLoading(true);
    setOpen(false);
    mutate({ examId, registrationId });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col gap-6 p-4"
      >
        <DialogHeader>
          <DialogTitle>
            Générer{" "}
            {registrationId === undefined ? "les certificats" : "le certificat"}
          </DialogTitle>
          <DialogDescription hidden={true}>
            Génération des certificats
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-row gap-2 w-full">
          <Button
            disabled={isPending}
            variant="ghostWithBorders"
            onClick={() => setOpen(false)}
            className="grow"
          >
            Annuler
          </Button>
          <Button onClick={onGenerate} disabled={isPending} className="grow">
            {isPending ? "En cours..." : "Générer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateCertificatesDialog;
